import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ContentContainer } from "../../../../../components/content-container/ContentContainer";
import { ContentSection } from "../../../../../components/content-section/ContentSection";
import { StepMenuStepProps } from "../../../../../components/step-menu/StepMenu";
import { useRouteContext } from "../../../../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../../../../queries/UseQueryNotifications";
import { useEffectiveRolePermissions } from "../../../../../queries/user-role-administration/UseEffectiveRolePermissions";
import PermissionsList, { PermissionsListTypeEnum } from "../../../common/permissions-list/PermissionsList";
import { useAccessFunctionality } from "../../../../../queries/access-control/UseAccessFunctionality";
import { usePermissions } from "../../../../../queries/access-control/UsePermissions";
import { UserFieldType } from "../UserFieldTypes";

export type SecurityStepProps = {
    organizationID: number | undefined;
} & StepMenuStepProps;

export default function SecurityStep({
    organizationID
}: SecurityStepProps) {

    const routeContext = useRouteContext();
    const { t } = useTranslation();
    const [expansion, setExpansion] = useState<{ [key: string]: boolean; }>({});

    const {
        getValues
    } = useFormContext<UserFieldType>();

    const user = getValues();
    const roleIDs = user.associatedRoles.map(r => r.id);

    const {
        data: permissionsData,
        status: permissionsStatus
    } = useQueryNotifications(useEffectiveRolePermissions)({
        groupID: routeContext.groupID as number,
        organizationID: organizationID as number,
        roleIDs: roleIDs,
        enabled: organizationID !== undefined
    });

    const permissions = usePermissions({
        objectSecurityData: permissionsData,
        ...useAccessFunctionality({
            groupID: routeContext.groupID as number,
            enabled: true
        })
    });

    return (
        <ContentContainer>
            <ContentSection
                sx={{
                    padding: 1,
                    overflowY: "auto"
                }}
            >
                <PermissionsList
                    type={(user.isElynxAdmin ?? false)
                        ? PermissionsListTypeEnum.ElynxAdminUser
                        : (permissionsStatus !== "loading" && (permissionsData?.length ?? 0) === 0)
                            ? PermissionsListTypeEnum.NoData
                            : PermissionsListTypeEnum.Custom
                    }
                    noDataTitle={t("userEditor.steps.security.noPermissions.title")}
                    noDataText={t("userEditor.steps.security.noPermissions.text")}
                    permissions={permissions}
                    expansionKey={"S"}
                    expansion={expansion}
                    toggleExpansion={key => {
                        setExpansion(prev => {
                            const copy = { ...prev };
                            if (copy[key]) {
                                delete copy[key];
                            } else {
                                copy[key] = !copy[key];
                            }
                            return copy;
                        });
                    }}
                />
            </ContentSection>
        </ContentContainer>
    );
}



